import { Component, ChangeDetectionStrategy, Input, OnInit } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { InstallationDetailsStore, InstallationFeaturesStore } from '@store';
import {
  ActivateDeactivateCommands,
  DeviceIds,
  EMSHocaFeatures,
  EmsHocaFeaturesProperties,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HemsConfigurationOverviewProperties,
  HemsType,
  IsActiveProperty,
  TariffBasedFinancialOptimizationStateCommands,
  TariffBasedFinancialOptimizationStateProperty,
} from '@vi-iot/client/index';
import { GTMService } from '@services';
import { AssistantSupportedLanguages } from 'src/app/@store/assistant';
import { DynamicAssistantPlan } from 'src/projects/client/apis/purchases/purchases.model';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-hems-financial-optimization',
  templateUrl: './hems-financial-optimization.component.html',
  styleUrls: ['./hems-financial-optimization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HemsFinancialOptimizationComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;
  readonly assistantSupportedLanguages = AssistantSupportedLanguages;
  readonly dynamicAssistantPlan = DynamicAssistantPlan;
  readonly bcg: { label: string; isSubscriptionActive: boolean } = {
    label: '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO',
    isSubscriptionActive: false,
  };
  readonly hemsType = HemsType;

  @Input() isForecastBasedActive: boolean;
  @Input() hemsGateway: string;
  @Input() featureTariffBasedFinancialOptimizationState: Feature<
    TariffBasedFinancialOptimizationStateProperty,
    TariffBasedFinancialOptimizationStateCommands
  >;
  @Input() featureAllowBatteryChargingFromTheGrid: Feature<
    IsActiveProperty,
    ActivateDeactivateCommands
  >;
  @Input({ required: true }) featureForecastBased: Feature<
    IsActiveProperty,
    ActivateDeactivateCommands
  >;
  @Input({ required: true }) hocaFeatures: Feature<EmsHocaFeaturesProperties, never>;

  hemsConfigurationOverview$: Observable<Feature<HemsConfigurationOverviewProperties, never>>;

  get noTariffsDataAvailableMessageKey(): string | null {
    const {
      subscriptionActive,
      tariffsActive,
    } = this.featureTariffBasedFinancialOptimizationState?.properties;

    const dynamicTariffSubscriptionActive = subscriptionActive?.value ?? false;
    const traiffDataAvailable = tariffsActive?.value ?? false;
    const hasBCG = !!this.featureAllowBatteryChargingFromTheGrid;

    if (
      dynamicTariffSubscriptionActive &&
      !traiffDataAvailable &&
      this.activeDAPlan === DynamicAssistantPlan.Basic &&
      hasBCG
    ) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.TARIFFS_ACTIVE.EXPLAINATION.FALSE_TEXT';
    }

    return null;
  }

  get bannerMessageKey(): string | null {
    const {
      subscriptionActive,
      tariffsActive,
    } = this.featureTariffBasedFinancialOptimizationState?.properties;
    const supported = this.hocaFeatures?.properties?.supported;

    const dynamicTariffSubscriptionActive = subscriptionActive?.value ?? false;
    const traiffDataAvailable = tariffsActive?.value ?? false;
    const hasBCG = !!this.featureAllowBatteryChargingFromTheGrid;
    const isUserEligibleForDAPRO =
      supported?.value.includes(EMSHocaFeatures.AllowBatteryChargingFromTheGrid) ||
      supported?.value.includes(EMSHocaFeatures.VehicleChargingStationSmartCharging);

    if (!dynamicTariffSubscriptionActive && !hasBCG && isUserEligibleForDAPRO) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.SUBSCRIPTION_ACTIVE.EXPLAINATION_DA_PRO';
    }

    if (!dynamicTariffSubscriptionActive && !hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.SUBSCRIPTION_ACTIVE.EXPLAINATION.FALSE_TEXT';
    }

    if (dynamicTariffSubscriptionActive && !traiffDataAvailable && !hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.TARIFFS_ACTIVE.EXPLAINATION.FALSE_TEXT';
    }

    if (!dynamicTariffSubscriptionActive && hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.TARRIF_DATA_BATTERY_CHARGING_SUBSCRIPTION_REQUIRED_TEXT';
    }

    if (
      dynamicTariffSubscriptionActive &&
      hasBCG &&
      this.activeDAPlan === DynamicAssistantPlan.Basic
    ) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.BATTERY_CHARGING_SUBSCRIPTION_REQUIRED_TEXT';
    }

    if (
      dynamicTariffSubscriptionActive &&
      !traiffDataAvailable &&
      hasBCG &&
      this.activeDAPlan === DynamicAssistantPlan.Pro
    ) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.BATTERY_CHARGING_TARIFF_ACTIVE.EXPLANATION.FALSE_TEXT';
    }

    return null;
  }

  activeDAPlan: DynamicAssistantPlan;

  readonly dynamicAssistantLabel$ = this.installationDetailsStore.activeDynamicAssistantPlan$.pipe(
    tap((plan: DynamicAssistantPlan) => {
      this.activeDAPlan = plan;
      this.setBCGSubcriptionStatus(plan);
    }),
    map((plan: DynamicAssistantPlan) => this.getDAPlanTranslationKey(plan))
  );

  constructor(
    public installationFeaturesStore: InstallationFeaturesStore,
    public installationDetailsStore: InstallationDetailsStore,
    private gtmService: GTMService
  ) {}

  ngOnInit(): void {
    this.hemsConfigurationOverview$ = this.installationFeaturesStore.selectInstallationGatewayFeature<
      HemsConfigurationOverviewProperties,
      never
    >(FeatureNames.hemsConfigurationOverview, this.hemsGateway);
  }

  onTariffBasedFinancialOptimizationChange(
    change: MatSlideToggleChange,
    feature: Feature<
      TariffBasedFinancialOptimizationStateProperty,
      TariffBasedFinancialOptimizationStateCommands
    >
  ): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      change.checked,
      FeaturePropertyKey.active,
      FeatureActionFields.active,
      FeatureCommands.setActive,
      this.hemsGateway,
      DeviceIds.Hems
    );
    this.gtmService.track(
      change.checked ? 'onActivate' : 'onDeactivate',
      'Tariff based financial optimization state change',
      'hems'
    );
  }

  onActivateDeactivateFeature(
    change: MatSlideToggleChange,
    feature: Feature<IsActiveProperty, ActivateDeactivateCommands>,
    gtmMessage: string
  ): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      {},
      FeaturePropertyKey.active,
      null,
      change.checked ? FeatureCommands.activate : FeatureCommands.deactivate,
      this.hemsGateway,
      DeviceIds.Hems
    );
    this.gtmService.track(change.checked ? 'onActivate' : 'onDeactivate', gtmMessage, 'hems');
  }

  private getDAPlanTranslationKey(plan: DynamicAssistantPlan): string {
    if (!plan) {
      if (this.featureAllowBatteryChargingFromTheGrid) {
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO';
      } else {
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_ON';
      }
    }

    switch (plan) {
      case DynamicAssistantPlan.Basic:
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_ON';
      case DynamicAssistantPlan.Pro:
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO';
      default:
        return '';
    }
  }

  private setBCGSubcriptionStatus(plan: DynamicAssistantPlan): void {
    if (!plan || plan === DynamicAssistantPlan.Basic) {
      this.bcg.isSubscriptionActive = false;
    } else {
      this.bcg.isSubscriptionActive = true;
    }
  }
}
